import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { slideLeft } from './login-page.animations';
import { LoginFormComponent } from './components/login-form/login-form.component';
import { RegisterFormComponent } from './components/register-form/register-form.component';
import { ForgotPasswordFormComponent } from './components/forgot-password-form/forgot-password-form.component';
import { UserService } from 'app/core/services/user.service';
import { AuthStateService } from 'app/core/services/auth-state.service';
import { AppConstants } from 'app/app.constants';
import { AppSharedModule } from 'app/app-shared.module';
import { resolveLocalizedLink } from 'app/utils/localized-link.utils';
import { HeaderComponent } from 'app/shared/components/header/header.component';

export type ActiveView = 'login' | 'register' | 'forgot-password';

@Component({
  standalone: true,
  selector: 'app-new-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
  animations: [slideLeft],
  imports: [
    CommonModule,
    TranslateModule,
    LoginFormComponent,
    RegisterFormComponent,
    ForgotPasswordFormComponent,
    AppSharedModule,
    HeaderComponent,
  ],
})
export class NewLoginPageComponent implements OnInit, OnDestroy {
  activeView: ActiveView = 'login';
  readonly appStoreLink = AppConstants.MOBILE_APP.APP_STORE_URL;
  readonly googlePlayLink = AppConstants.MOBILE_APP.GOOGLE_PLAY_URL;

  private destroy$ = new Subject<void>();
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private userService = inject(UserService);
  private translate = inject(TranslateService);
  private toastr = inject(ToastrService);
  private readonly authState = inject(AuthStateService);

  ngOnInit(): void {
    // Initialize default language if not set
    const selectedLanguage = localStorage.getItem(
      AppConstants.KEYS_LOCAL_STORAGE.ISO_LANG,
    );
    if (!selectedLanguage) {
      localStorage.setItem(
        AppConstants.KEYS_LOCAL_STORAGE.ISO_LANG,
        AppConstants.LANGUAGES.PT_BR,
      );
    }

    // Clear stale tokens on first visit (alternating pattern from original auth page)
    if (!localStorage.getItem(AppConstants.KEYS_LOCAL_STORAGE.FIRST_LOAD)) {
      localStorage.setItem(AppConstants.KEYS_LOCAL_STORAGE.FIRST_LOAD, 'true');
      this.authState.clearSession();
    } else {
      localStorage.removeItem(AppConstants.KEYS_LOCAL_STORAGE.FIRST_LOAD);
    }

    // Handle email confirmation hash from registration link
    this.confirmFreeTierRegistration();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Digital agriculture page of the company site, in the app language. */
  get digitalAgricultureLink(): string {
    return resolveLocalizedLink(
      AppConstants.EXTERNAL_LINKS.COMPANY_DIGITAL_AGRICULTURE,
      this.translate.currentLang,
    );
  }

  private confirmFreeTierRegistration(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const hash = params['hash'];
        if (!hash) return;

        this.userService.confirmarCadastroUsuarioFreeTier(hash).subscribe({
          next: () => {
            this.translate
              .get('global.alert.auth-account-register')
              .subscribe((res: string) => {
                this.toastr.success(res, '', {
                  positionClass: 'toast-top-full-width',
                  disableTimeOut: false,
                });
              });
            setTimeout(() => {
              this.router.navigate(['login']);
            }, 3000);
          },
          error: (error) => {
            if (error.status === 304) {
              this.translate
                .get('global.alert.auth-account-register')
                .subscribe((res: string) => {
                  this.toastr.success(res, '', {
                    positionClass: 'toast-top-full-width',
                    disableTimeOut: false,
                  });
                });
            } else {
              this.translate
                .get('global.alert.auth-account-register-error')
                .subscribe((res: string) => {
                  this.toastr.error(res, '', {
                    positionClass: 'toast-top-full-width',
                    disableTimeOut: false,
                  });
                });
            }
          },
        });
      });
  }
}
